import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClickAwayListener, Fab } from '@mui/material';
import { motion, useDragControls, useMotionValue } from 'framer-motion';

import {
  assistantChatProcessedIdsAtom,
  calendarViewSwitcherAtom,
  useAssistantChat,
} from '../../../data-access';
import { ReactComponent as AssistantOpen } from '../../../assets/assistant/fab-open.svg';
import { ReactComponent as AssistantClose } from '../../../assets/assistant/fab-close.svg';

import { AssistantChatContent } from '../AssistantChatContent';
import { AssistantChatScheduler } from '../AssistantChatScheduler';
import { useResizable, useDraggableFab } from '../../../utils/hooks';

import './AssistantChatFab.scss';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useLocation } from 'react-router-dom';

const FAB_SIZE = 48;
const GAP = 12;
const PADDING = 8;
const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 560;
const MIN_WIDTH = 356;
const MIN_HEIGHT = 520;
const MAX_WIDTH = 600;
const MAX_HEIGHT = 700;
const DEFAULT_BOTTOM = 60;
const DEFAULT_RIGHT = 500;
const FAB_VIEW_MARGIN = 16; // horizontal keep-in-view for FAB and content

const STORAGE_KEY = 'assistant-chat-fab-position';

interface AssistantChatFabProps {
  hideFab?: boolean;
}

const AssistantChatFab: FC<AssistantChatFabProps> = ({ hideFab = false }) => {
  const {
    fabPos,
    constraints,
    hydrated,
    onDragStart,
    onDrag,
    onDragEnd,
    calculateMenuLayout,
    calculateMenuLayoutFor,
    shouldPreventClick,
    x,
    y,
    setFabPosition,
  } = useDraggableFab({
    fabSize: FAB_SIZE,
    defaultBottom: DEFAULT_BOTTOM,
    defaultRight: DEFAULT_RIGHT,
    storageKey: STORAGE_KEY,
    gap: GAP,
    padding: PADDING,
  });

  const [processedIds, setProcessedIds] = useRecoilState(assistantChatProcessedIdsAtom);
  const ignoreNextToggleRef = useRef(false);
  const location = useLocation();
  const calendarView = useRecoilValue(calendarViewSwitcherAtom);
  const previousPathnameRef = useRef(location.pathname);
  const previousCalendarViewRef = useRef(calendarView);

  const { size: menuSize, handleMouseDown } = useResizable({
    initialSize: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
    constraints: {
      minWidth: MIN_WIDTH,
      minHeight: MIN_HEIGHT,
      maxWidth: MAX_WIDTH,
      maxHeight: MAX_HEIGHT,
    },
  });

  const {
    openAssistantChat,
    setOpenAssistantChat,
    messages,
    startPlanChat,
    isSchedulerMode,
    clearMessages,
  } = useAssistantChat();

  // Allow dragging the chat window using the drag indicator in header
  const overlayDragControls = useDragControls();
  const overlayX = useMotionValue(0);
  const overlayY = useMotionValue(0);
  const [isOverlayDragging, setIsOverlayDragging] = useState(false);

  const memoizedHandleChatStart = useCallback(() => {
    if (messages.length === 0 && !isSchedulerMode) {
      startPlanChat();
    }
  }, [messages.length, isSchedulerMode, startPlanChat]);

  useEffect(() => {
    if (openAssistantChat) {
      memoizedHandleChatStart();
    }
  }, [openAssistantChat, memoizedHandleChatStart]);

  useEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== location.pathname;
    const calendarViewChanged = previousCalendarViewRef.current !== calendarView;

    if (!pathnameChanged && !calendarViewChanged) {
      return;
    }

    previousPathnameRef.current = location.pathname;
    previousCalendarViewRef.current = calendarView;

    if (openAssistantChat) {
      setOpenAssistantChat(false);
    }
  }, [
    calendarView,
    location.pathname,
    openAssistantChat,
    setOpenAssistantChat,
  ]);

  const handleOpenAssistantChat = useCallback(() => {
    if (ignoreNextToggleRef.current) {
      ignoreNextToggleRef.current = false;
      return;
    }
    if (shouldPreventClick() || isOverlayDragging) {
      return;
    }
    setOpenAssistantChat(!openAssistantChat);
  }, [
    setOpenAssistantChat,
    openAssistantChat,
    shouldPreventClick,
    isOverlayDragging,
  ]);

  const handleRightMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleMouseDown(e, 'right');
    },
    [handleMouseDown]
  );

  const handleLeftMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleMouseDown(e, 'left');
    },
    [handleMouseDown]
  );

  const menuLayout = useMemo(() => {
    return calculateMenuLayout(menuSize.width, menuSize.height);
  }, [calculateMenuLayout, menuSize.width, menuSize.height]);
  const latestMenuLayoutRef = useRef(menuLayout);
  useEffect(() => {
    latestMenuLayoutRef.current = menuLayout;
  }, [menuLayout]);

  // Reset overlay offset only when chat (re)opens
  useEffect(() => {
    if (openAssistantChat) {
      overlayX.set(0);
      overlayY.set(0);
    }
  }, [openAssistantChat, overlayX, overlayY]);

  const handleOverlayDragStart = useCallback(() => {
    setIsOverlayDragging(true);
  }, []);

  const handleOverlayDragEnd = useCallback(() => {
    setIsOverlayDragging(false);
    // Optional: clamp within viewport
    try {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const padding = PADDING;
      const contentMarginX = FAB_VIEW_MARGIN; // horizontal margin for chat content
      const contentMarginY = padding; // vertical margin for chat content (minimal)
      const minX = contentMarginX - menuLayout.left;
      const maxX = Math.max(contentMarginX, vw - menuSize.width - contentMarginX) - menuLayout.left;
      const minY = contentMarginY - menuLayout.top;
      const maxY = Math.max(contentMarginY, vh - menuSize.height - contentMarginY) - menuLayout.top;

      const curX = overlayX.get();
      const curY = overlayY.get();

      const clampedX = Math.max(minX, Math.min(curX, maxX));
      const clampedY = Math.max(minY, Math.min(curY, maxY));

      // Compute absolute drop position
      const dropAbsLeft = menuLayout.left + clampedX;
      const dropAbsTop = menuLayout.top + clampedY;

      // Compute FAB position to sit beside the overlay at drop position
      const minFabX = FAB_VIEW_MARGIN;
      const maxFabX = Math.max(FAB_VIEW_MARGIN, vw - FAB_SIZE - FAB_VIEW_MARGIN);
      const minFabY = FAB_VIEW_MARGIN;
      const maxFabY = Math.max(FAB_VIEW_MARGIN, vh - FAB_SIZE - FAB_VIEW_MARGIN);

      // Candidates: FAB on left of overlay (overlay to right), or FAB on right of overlay (overlay to left)
      const fabXLeftCandidate = dropAbsLeft - GAP - FAB_SIZE; // FAB left of overlay
      const fabXRightCandidate = dropAbsLeft + menuSize.width + GAP; // FAB right of overlay

      const clampX = (v: number) => Math.max(minFabX, Math.min(v, maxFabX));
      const candLeftClamped = clampX(fabXLeftCandidate);
      const candRightClamped = clampX(fabXRightCandidate);
      const leftError = Math.abs(candLeftClamped - fabXLeftCandidate);
      const rightError = Math.abs(candRightClamped - fabXRightCandidate);

      const useLeftSide = leftError <= rightError; // prefer side with less clamp adjustment
      const targetFabXFinal = useLeftSide ? candLeftClamped : candRightClamped;

      // Align FAB vertically with overlay center
      const fabYTargetRaw = dropAbsTop + menuSize.height / 2 - FAB_SIZE / 2;
      const targetFabYFinal = Math.max(minFabY, Math.min(fabYTargetRaw, maxFabY));

      // Pre-compute layout for the new FAB position to avoid a visible jump
      const targetLayout = calculateMenuLayoutFor(
        targetFabXFinal,
        targetFabYFinal,
        menuSize.width,
        menuSize.height
      );

      const proposedX = dropAbsLeft - targetLayout.left;
      const proposedY = dropAbsTop - targetLayout.top;

      // If keeping exact drop would cover the FAB, snap overlay to anchor instead
      const overlayLeft = targetLayout.left + proposedX;
      const overlayTop = targetLayout.top + proposedY;
      const overlayRight = overlayLeft + menuSize.width;
      const overlayBottom = overlayTop + menuSize.height;
      const fabLeft = targetFabXFinal;
      const fabTop = targetFabYFinal;
      const fabRight = fabLeft + FAB_SIZE;
      const fabBottom = fabTop + FAB_SIZE;
      const overlapsFab = !(
        overlayRight <= fabLeft ||
        overlayLeft >= fabRight ||
        overlayBottom <= fabTop ||
        overlayTop >= fabBottom
      );

      if (overlapsFab) {
        overlayX.set(0);
        overlayY.set(0);
      } else {
        overlayX.set(proposedX);
        overlayY.set(proposedY);
      }

      // Finally move the FAB (with spring), overlay offsets already adjusted for the new anchor
      setFabPosition(targetFabXFinal, targetFabYFinal);
    } catch {
      // no-op
    }
  }, [
    menuLayout.left,
    menuLayout.top,
    menuSize.width,
    menuSize.height,
    overlayX,
    overlayY,
    setFabPosition,
    fabPos.x,
    fabPos.y,
  ]);

  const handleOverlayPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      const target = e.target as HTMLElement | null;
      if (target?.closest('.assistant-chat-fab__resize-handle')) {
        return;
      }
      overlayDragControls.start(e);
    },
    [overlayDragControls]
  );

  if (!hydrated) return null;

  return (
    <>
      {!hideFab && (
        <motion.div
          drag
          dragElastic={0}
          dragMomentum={false}
          dragConstraints={constraints}
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: FAB_SIZE,
            height: FAB_SIZE,
            x,
            y,
            pointerEvents: 'auto',
            zIndex: 9999,
          }}
          className="assistant-chat-fab"
        >
          <Fab
            color="primary"
            className="assistant-chat-fab__fab-button"
            sx={{ width: FAB_SIZE, height: FAB_SIZE, minHeight: FAB_SIZE }}
            onClick={handleOpenAssistantChat}
          >
            {openAssistantChat ? <AssistantClose /> : <AssistantOpen />}
          </Fab>
        </motion.div>
      )}
      {openAssistantChat && <div className="assistant-chat-fab__backdrop" />}
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={() => {
          if (shouldPreventClick() || isOverlayDragging || !openAssistantChat) {
            return;
          }
          ignoreNextToggleRef.current = true;
          setOpenAssistantChat(false);
          setTimeout(() => {
            ignoreNextToggleRef.current = false;
          }, 200);
        }}
      >
        <motion.div
          className="assistant-chat-fab__tooltip-container"
          style={{
            display: openAssistantChat ? 'block' : 'none',
            position: 'fixed',
            left: menuLayout.left,
            top: menuLayout.top,
            zIndex: 10000,
            pointerEvents: 'auto',
            x: overlayX,
            y: overlayY,
            willChange: 'transform',
          }}
          drag
          dragElastic={0}
          dragMomentum={false}
          dragListener={false}
          dragControls={overlayDragControls}
          onDragStart={handleOverlayDragStart}
          onDragEnd={handleOverlayDragEnd}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onPointerDown={handleOverlayPointerDown}
        >
          <div className="assistant-chat-fab__tooltip">
            <div
              className="assistant-chat-fab__tooltip-content"
              style={{ width: `${menuSize.width}px`, height: `${menuSize.height}px` }}
            >
              {isSchedulerMode ? (
                <AssistantChatScheduler
                  onClear={clearMessages}
                  setProcessedIds={setProcessedIds}
                  containerHeight={menuSize.height}
                />
              ) : (
                <AssistantChatContent
                  processedIds={processedIds}
                  setProcessedIds={setProcessedIds}
                  onClear={clearMessages}
                  containerHeight={menuSize.height}
                  hideHeader={false}
                />
              )}

              <div
                className="assistant-chat-fab__resize-handle assistant-chat-fab__resize-handle--right"
                onMouseDown={handleRightMouseDown}
              />
              <div
                className="assistant-chat-fab__resize-handle assistant-chat-fab__resize-handle--left"
                onMouseDown={handleLeftMouseDown}
              />
            </div>
          </div>
        </motion.div>
      </ClickAwayListener>
    </>
  );
};

export default AssistantChatFab;
